import { Mailbox } from '../../models/mailboxes.model';
import { ErrorCode, TechnicalError } from '../../models/errors.model';
import { SuccessCode, SuccessInfo } from '../../models/success.model';
import * as MailboxActions from '../actions/mailbox.actions';

export interface MailboxState {
  cdbId: string;
  entries: Mailbox[];
  selectedMailbox: Mailbox;
  createMailboxLink?: string;
  showCreateMailboxLink?: boolean;
  error: TechnicalError;
  success: SuccessInfo;
  openedMailbox: Mailbox;
}

export const initialState: MailboxState = {
  cdbId: null,
  entries: [],
  selectedMailbox: null,
  createMailboxLink: null,
  error: null,
  success: null,
  openedMailbox: null,
};

export function reducer(
  state = initialState,
  action: MailboxActions.Actions,
): MailboxState {
  switch (action.type) {
    case MailboxActions.ActionTypes.LOADED:
      return loadedMailboxes(state, action);
    case MailboxActions.ActionTypes.LOAD_FAIL:
      return failedLoading(state, action);
    case MailboxActions.ActionTypes.SELECT:
      return mailboxSelected(state, action);
    case MailboxActions.ActionTypes.ACTIVATE:
      return activateSelectedMailbox(state, action);
    case MailboxActions.ActionTypes.CREATE_SUCCESS:
      return createdMailbox(state, action);
    case MailboxActions.ActionTypes.CREATE_FAIL:
      return failedCreatingMailbox(state, action);
    case MailboxActions.ActionTypes.RESET_SUCCESS:
      return resetSuccess(state, action);
    case MailboxActions.ActionTypes.RESET_ERROR:
      return resetError(state, action);
    case MailboxActions.ActionTypes.OPEN:
      return openedMailbox(state, action);
    default:
      return state;
  }
}

function loadedMailboxes(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  const mailboxes = action.payload.mailboxes;
  return {
    ...state,
    cdbId: action.payload.cdbId,
    entries: mailboxes,
    selectedMailbox: state.selectedMailbox
      ? state.selectedMailbox
      : mailboxes[0],
    createMailboxLink:
      action.payload._links && action.payload._links['mbx:createMailbox']
        ? action.payload._links['mbx:createMailbox'].href
        : false,
    showCreateMailboxLink:
      !mailBoxesWithPendingStatusExists(mailboxes) &&
      action.payload._links &&
      action.payload._links['mbx:createMailbox']
        ? action.payload._links['mbx:createMailbox'].href
        : false,
    error:
      mailboxes.length > 0
        ? null
        : {
            code: ErrorCode.MailBoxesNotFound,
            message: null,
          },
  };
}

function failedLoading(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    success: null,
    error: action.payload,
  };
}

function mailboxSelected(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    selectedMailbox: action.payload,
  };
}

function mailBoxesWithPendingStatusExists(mailboxes: Mailbox[]): boolean {
  return (
    mailboxes.filter((mailbox: Mailbox) => mailbox.status === 'pending')
      .length > 0
  );
}

function activateSelectedMailbox(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  const selectedMailbox = Object.assign({}, action.payload, {
    status: undefined,
  });

  const newEntries = state.entries.map((entry, index) =>
    entry.mbxId === action.payload.mbxId
      ? Object.assign({}, entry, { status: undefined })
      : entry,
  );

  return {
    ...state,
    entries: newEntries,
    selectedMailbox,
    showCreateMailboxLink:
      !mailBoxesWithPendingStatusExists(newEntries) &&
      !!state.createMailboxLink,
  };
}

function createdMailbox(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    error: null,
    success: {
      code: SuccessCode.MailboxCreated,
    },
  };
}

function failedCreatingMailbox(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    success: null,
    error: action.payload,
  };
}

function resetSuccess(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    success: null,
  };
}

function resetError(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    error: null,
  };
}

function openedMailbox(
  state: MailboxState,
  action: MailboxActions.Actions,
): MailboxState {
  return {
    ...state,
    openedMailbox: action.payload,
  };
}

export const getMailboxEntries = (state: MailboxState) => state.entries;
export const getShowCreateMailboxLink = (state: MailboxState) =>
  state.showCreateMailboxLink;
export const getSelectedMailbox = (state: MailboxState) =>
  state.selectedMailbox;
export const getMailboxesError = (state: MailboxState) => state.error;
export const getMailboxesSuccess = (state: MailboxState) => state.success;
export const getOpenedMailbox = (state: MailboxState) => state.openedMailbox;
